import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useTheme } from '../../../../theme-context';
import { PartnerGuard } from '../../../../guards/RolesGuard';
import { TRANSACTIONS_STATUSES } from '../../../../utils/constants/transactions-statuses';
import { TRANSACTIONS_TYPE } from '../../../../utils/constants/insights-filter';
import TableActions from '../../../../shared/table/table-actions';

const TransactionsTable = ({
    rows,
    columns,
    selectedStatuses,
    paginationData,
    paginationState,
    setPaginationState,
    sortSelected,
    hideActions,
    handleStatusToggle,
    handleSortChange,
    onOpenTransactionDetails,
    onClickShowTransactionModal,
}: {
    rows?: any;
    columns?: any;
    selectedStatuses?: any;
    paginationData?: any;
    paginationState?: any;
    setPaginationState?: any;
    sortSelected?: any;
    hideActions?: boolean;
    handleStatusToggle?: (status: string) => void;
    handleSortChange?: (sortOrder: string, type: string) => void;
    onOpenTransactionDetails?: (row: any) => void;
    onClickShowTransactionModal?: (row: any) => void;
}) => {
    const { theme } = useTheme();
    const statusFilterRef = useRef<any>(null);
    const statusFilterRef1 = useRef<any>(null);
    const statusFilterRef2 = useRef<any>(null);
    const posRef = useRef<any>(null);
    const pspRef = useRef<any>(null);
    const [filterOpen, setFilterOpen] = useState<any>();
    const [isVarianceFilterOpen, setIsVarianceFilterOpen] = useState(false);
    const [isPspFilterOpen, setIsPspFilterOpen] = useState(false);
    const [isPosFilterOpen, setIsPosFilterOpen] = useState(false);
	const [isReasonFilterOpen, setIsReasonFilterOpen] = useState(false);
	const [isReferenceFilterOpen, setIsReferenceFilterOpen] = useState(false);
    const [openedTransactions, setOpenedTransactions] = useState<any>();
    const [orderedColumns, setOrderedColumns] = useState(columns || []);
    const draggedColumnIndex = useRef<number | null>(null);

    useEffect(() => {
        if (!columns?.length) return;

        const savedOrder = localStorage.getItem('transactionsTableColumnOrder');
        if (savedOrder) {
            try {
                const savedKeys: string[] = JSON.parse(savedOrder);
                const savedColumns = savedKeys
                    .map((key) => columns.find((col: any) => col.key === key))
                    .filter(Boolean);
                const newColumns = columns.filter(
                    (col: any) => !savedKeys.includes(col.key)
                );
                setOrderedColumns([...savedColumns, ...newColumns]);
            } catch (e) {
                console.error('Invalid column order in localStorage. Resetting to default.');
                setOrderedColumns(columns);
            }
        } else {
            setOrderedColumns(columns);
        }
    }, [columns]);

    const onDragStart = (index: number) => {
        draggedColumnIndex.current = index;
    };

    const onDrop = (index: number) => {
        if (draggedColumnIndex.current === null || draggedColumnIndex.current === index) return;

        const newCols = [...orderedColumns];
        const [movedCol] = newCols.splice(draggedColumnIndex.current, 1);
        newCols.splice(index, 0, movedCol);

        setOrderedColumns(newCols);

        const orderedKeys = newCols.map((col) => col.key);
        localStorage.setItem('transactionsTableColumnOrder', JSON.stringify(orderedKeys));

        draggedColumnIndex.current = null;
    };

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (statusFilterRef?.current && !statusFilterRef?.current?.contains(event.target)) {
                setIsPosFilterOpen(false);
            }
            if (statusFilterRef1?.current && !statusFilterRef1?.current?.contains(event.target)) {
                setIsPspFilterOpen(false);
            }
            if (statusFilterRef2?.current && !statusFilterRef2?.current?.contains(event.target)) {
                setIsVarianceFilterOpen(false);
            }
            if (pspRef?.current && !pspRef?.current?.contains(event.target)) {
                setFilterOpen((prev: any) => ({
                    ...prev || {},
                    amountPSP: false
                }));
            }
            if (posRef?.current && !posRef?.current?.contains(event.target)) {
                setFilterOpen((prev: any) => ({
                    ...prev || {},
                    amountPOS: false
                }));
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const formatStatus = (status: string) => {
        if (status !== 'voided transaction') {
            const findTransactionStatus = TRANSACTIONS_STATUSES?.find((item: any) => item?.value === status)

            return <div className={`flex items-center px-3 py-1 ${findTransactionStatus?.statusColor} rounded-full justify-center`}>
                <p className={`${findTransactionStatus?.color} font-normal text-nowrap`}>{findTransactionStatus?.name}</p>
            </div>
        } else {
            return <div className={`flex items-center px-3 py-1 bg-[#545863] rounded-md justify-center`}>
                <p className={`text-white font-normal text-nowrap`}>Voided</p>
            </div>
        }
    }

    const getColorAndIcon = (color: string) => {
        switch (color) {
            case 'Green':
            case 'green':
                return { color: 'text-[#10B981]', icon: '/assets/insights-tab/matched-table.svg' }
            case 'Red':
            case 'red':
                return { color: 'text-[#EF4444]', icon: '/assets/insights-tab/discrepancy-table.svg' }
            case 'Amber':
            case 'amber':
                return { color: 'text-[#F59E0B]', icon: '/assets/insights-tab/likely-matched-table.svg' }
            case 'Blue':
            case 'blue':
                return { color: 'text-[#2D8EFF]', icon: '/assets/insights-tab/likely-matched-table.svg' }
        }
    }

    const onToggleTransaction = (id: string, hide: boolean) => {
        if (hide) {
            const filterTransactions = openedTransactions?.length > 0 && openedTransactions?.filter((item: string) => item !== id)
            setOpenedTransactions(filterTransactions)
        } else {
            setOpenedTransactions([...openedTransactions || [], id])
        }
    }

    const formatText = (text: string | null | undefined): string => {
        if (!text) return '';

        return text
            .toLowerCase()
            .split(' ')
            .map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join(' ');
    };

    return (
        <div className=''>
            <div className='overflow-x-auto border border-1 !rounded-xl dark:border-borderColor-darkMode'>
                <table className='min-w-full bg-white dark:bg-black dark:text-primary-dark'>
                    <thead className='bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-primary-dark rounded-xl'>
                        <tr>
                            {orderedColumns && orderedColumns?.length > 0 && orderedColumns?.map((item: any, idx: number) => {
                                return (
                                    <th
                                        draggable
                                        data-column-index={idx}
                                        onDragStart={() => onDragStart(idx)}
                                        onDragOver={onDragOver}
                                        onDrop={() => onDrop(idx)}
                                        key={idx}
                                        className={`px-4 py-3 cursor-move ${(item?.name === 'PSP Date' || item?.name === 'POS Date' || item?.name === 'Variance' || item?.name === 'Matching Reference') ? 'min-w-[130px]' : ''}`}>
                                        <div className='flex flex-row items-start'>
                                            <p className='w-[130px]'>{item?.name}</p>
											{(item?.name === 'Matching Reference') && (
                                                <div className='relative ml-2' ref={statusFilterRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80 w-5'
                                                        onClick={() => setIsReferenceFilterOpen(!isReferenceFilterOpen)}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {isReferenceFilterOpen && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('new', 'reference')}
                                                                    className={`block ${sortSelected?.reference === 'new' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort New to Old
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('old', 'reference')}
                                                                    className={`block ${sortSelected?.reference === 'old' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Old to New
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
											{(item?.name === 'Reason') && (
                                                <div className='relative ml-2' ref={statusFilterRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80 w-5'
                                                        onClick={() => setIsReasonFilterOpen(!isReasonFilterOpen)}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {isReasonFilterOpen && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('new', 'reason')}
                                                                    className={`block ${sortSelected?.pos === 'new' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort New to Old
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('old', 'reason')}
                                                                    className={`block ${sortSelected?.pos === 'old' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Old to New
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(item?.name === 'POS Date') && (
                                                <div className='relative ml-2' ref={statusFilterRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80 w-5'
                                                        onClick={() => setIsPosFilterOpen(!isPosFilterOpen)}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {isPosFilterOpen && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('new', 'pos')}
                                                                    className={`block ${sortSelected?.pos === 'new' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort New to Old
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('old', 'pos')}
                                                                    className={`block ${sortSelected?.pos === 'old' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Old to New
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(item?.name === 'PSP Date') && (
                                                <div className='relative ml-2' ref={statusFilterRef1}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80 w-5'
                                                        onClick={() => setIsPspFilterOpen(!isPspFilterOpen)}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {isPspFilterOpen && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('new', 'psp')}
                                                                    className={`block ${sortSelected?.psp === 'new' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort New to Old
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('old', 'psp')}
                                                                    className={`block ${sortSelected?.psp === 'old' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Old to New
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}

                                                </div>
                                            )}
                                            {item?.name === 'Variance' &&
                                                <div className='relative ml-2' ref={statusFilterRef2}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80 w-5'
                                                        onClick={() => setIsVarianceFilterOpen(!isVarianceFilterOpen)}>
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    <div>
                                                        {isVarianceFilterOpen && (
                                                            <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                                <div className='space-y-1'>
                                                                    <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                    <button
                                                                        onClick={() => handleSortChange?.('big', 'variance')}
                                                                        className={`block ${sortSelected?.variance === 'big' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                    >
                                                                        Sort Biggest to Smallest
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleSortChange?.('small', 'variance')}
                                                                        className={`block ${sortSelected?.variance === 'small' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                    >
                                                                        Sort Smallest to Biggest
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            }
                                            {(item?.name === 'PSP Value') && (
                                                <div className='relative ml-2' ref={pspRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80 w-5'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            amountPSP: !filterOpen?.amountPSP
                                                        }
                                                        ))}                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.amountPSP && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('big', 'pspValue')}
                                                                    className={`block ${sortSelected?.pspValue === 'big' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Biggest to Smallest
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('small', 'pspValue')}
                                                                    className={`block ${sortSelected?.pspValue === 'small' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Smallest to Biggest
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(item?.name === 'POS Value') && (
                                                <div className='relative ml-2' ref={posRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80 w-5'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            amountPOS: !filterOpen?.amountPOS
                                                        }
                                                        ))}                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.amountPOS && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('big', 'posValue')}
                                                                    className={`block ${sortSelected?.posValue === 'big' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Biggest to Smallest
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('small', 'posValue')}
                                                                    className={`block ${sortSelected?.posValue === 'small' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Smallest to Biggest
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                )
                            })}
                            {!hideActions && <th className='px-4 py-3'>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows?.map((row: any) => {
                            const findifOpened = openedTransactions?.length > 0 && openedTransactions?.find((transaction: string) => transaction === row?.transactionId)
                            const transactionOpened = findifOpened ? TRANSACTIONS_STATUSES?.find((status: any) => status?.name === row?.status) : undefined
                            return (
                                <>
                                    <tr key={row.id} className={`${transactionOpened ? transactionOpened?.backColor : 'hover:bg-gray-50 dark:hover:bg-bgColor-darkMode'}  text-sm`}>
                                        {orderedColumns && orderedColumns?.length > 0 && orderedColumns?.map((item: any, idx: number) => {
                                            if (item?.key === 'variance') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 ${getColorAndIcon(row?.rag)?.color}`}>€{row?.[item?.key] || 0}</td>
                                                )
                                            }
                                            if (item?.key === 'status') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>{formatStatus(row?.[item?.key])}</td>
                                                )
                                            }
                                            if (item?.key === 'reason') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>{formatText(row?.[item?.key])}</td>
                                                )
                                            }
                                            if (item?.key === 'type') {
                                                const findTransaction = row?.[item?.key] ? TRANSACTIONS_TYPE?.find((transaction: any) => transaction?.value === row?.[item?.key])?.label : row?.[item?.key] || '--'
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>{findTransaction}</td>
                                                )
                                            }
                                            if (item?.key === 'transactionId') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>
                                                        <div
                                                            className='flex flex-row items-center'>
                                                            {(row?.multipleReferenceMatches && row?.multipleReferenceMatches?.length > 0) &&
                                                                <div
                                                                    className='cursor-pointer hover:opacity-80 min-w-[18px]'
                                                                    onClick={() => onToggleTransaction(row?.transactionId, !!findifOpened)}>
                                                                    <img src={
                                                                        theme === 'dark' ?
                                                                            findifOpened ? '/assets/table-actions/close-white.svg' : '/assets/table-actions/add-white.svg'
                                                                            : findifOpened ? '/assets/table-actions/close.svg' : '/assets/table-actions/add.svg'} className='object-contain w-[18px] mr-2' />
                                                                </div>
                                                            }
                                                            <p>{row?.transactionId}</p>
                                                        </div>
                                                    </td>
                                                )
                                            }
                                            if (item?.key === 'rag') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>
                                                        <div className='flex flex-row items-center'>
                                                            <img src={getColorAndIcon(row?.rag)?.icon} className='object-contain w-[18px] mr-2' />
                                                            <p>{row?.rag}</p>
                                                        </div>
                                                    </td>
                                                )
                                            }
                                            if (item?.key === 'amountPOS' || item?.key === 'amountPSP') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 min-w-[120px]`}>
                                                        <p>{['amountPOS', 'amountPSP']?.includes(item?.key) ? '€' : ''}{Number(row?.[item.key] || 0).toFixed(2)}</p>
                                                        {/* <p className='text-primary-light text-sm'>{row?.[item?.secondKey] ? moment(row?.[item?.secondKey]).format('DD/MM/YYYY hh:mm A') : ''}</p> */}
                                                    </td>
                                                )
                                            }
                                            if (item?.key === 'pspDateTime' || item?.key === 'posDateTime') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 min-w-[120px]`}>
                                                        <p className='text-sm'>{row?.[item?.key] ? moment(row?.[item?.key]).format('DD/MM/YYYY hh:mm A') : '--'}</p>
                                                    </td>
                                                )
                                            }
                                            else {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 ${['dateTime', 'dateAnalyzed']?.includes(item?.key) ? 'min-w-[120px]' : ''} ${item?.key === 'description' ? 'text-primary-light' : ''}`}>{['amountPOS', 'amountPSP']?.includes(item?.key) ? '€' : ''}{row?.[item?.key] || '--'}</td>
                                                )
                                            }
                                        })}
                                        {!hideActions &&
                                            <td className={`px-4 py-4`}>
                                                <TableActions
                                                    containerStyle='!mt-[-10px] !mr-[-20px] !rounded-sm dark:bg-gray-800'>
                                                    <div
                                                        onClick={() => onClickShowTransactionModal?.(row)}
                                                        className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                        <p className='dark:text-primary-dark'>View details</p>
                                                    </div>
                                                    {((row?.status || '').toLowerCase() !== 'pending' && (['unreconciled', 'reconciled', 'potential match']?.includes?.((row?.status || '').toLowerCase())) && PartnerGuard()) &&
                                                        <div
                                                            onClick={() => onOpenTransactionDetails?.(row)}
                                                            className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                            <p className='text-[#2D8EFF]'>Review Reconciliation</p>
                                                        </div>
                                                    }
                                                </TableActions>
                                            </td>
                                        }
                                    </tr>
                                    {findifOpened && row?.multipleReferenceMatches?.length > 0 && row?.multipleReferenceMatches?.map((child: any, childIdx: number) => (
                                        <tr key={`${row.transactionId}-child-${childIdx}`} className={`${transactionOpened ? transactionOpened?.backColor : 'hover:bg-gray-50 dark:hover:bg-bgColor-darkMode'} text-sm`}>
                                            {orderedColumns?.map((item: any, idx: number) => {
                                                if (item.key === 'variance') {
                                                    return (
                                                        <td key={idx} className={`px-4 py-4 ${getColorAndIcon(child?.rag)?.color}`}>
                                                            €{child?.[item.key] || 0}
                                                        </td>
                                                    );
                                                }
                                                if (item.key === 'status') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4'>{formatStatus(child?.[item.key])}</td>
                                                    );
                                                }
                                                if (item.key === 'reason') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4'>{formatText(child?.[item.key])}</td>
                                                    );
                                                }
                                                if (item?.key === 'type') {
                                                    const findTransaction = child?.[item?.key] ? TRANSACTIONS_TYPE?.find((transaction: any) => transaction?.value === child?.[item?.key])?.label : child?.[item?.key] || '--'
                                                    return (
                                                        <td key={idx} className={`px-4 py-4`}>{findTransaction}</td>
                                                    )
                                                }
                                                if (item.key === 'transactionId') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4 pl-8 text-sm text-primary-light'>
                                                            {child?.transactionId}
                                                        </td>
                                                    );
                                                }

                                                if (item.key === 'rag') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4'>
                                                            <div className='flex items-center'>
                                                                <img src={getColorAndIcon(child?.rag)?.icon} className='w-[18px] mr-2' />
                                                                <p>{child?.rag}</p>
                                                            </div>
                                                        </td>
                                                    );
                                                }
                                                if (item.key === 'reason') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4'>{formatText(row?.[item.key])}</td>
                                                    );
                                                }
                                                if (item.key === 'amountPOS' || item.key === 'amountPSP') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4 min-w-[120px]'>
                                                            <p>€{Number(child?.[item.key] || 0).toFixed(2)}</p>
                                                            {/* <p className='text-primary-light text-sm'>{row?.[item?.secondKey] ? moment(row?.[item?.secondKey]).format('DD/MM/YYYY hh:mm A') : ''}</p> */}
                                                        </td>
                                                    );
                                                }
                                                if (item?.key === 'pspDateTime' || item?.key === 'posDateTime') {
                                                    return (
                                                        <td key={idx} className={`px-4 py-4 min-w-[120px]`}>
                                                            <p className='text-sm'>{child?.[item?.key] ? moment(child?.[item?.key]).format('DD/MM/YYYY hh:mm A') : '--'}</p>
                                                        </td>
                                                    )
                                                }
                                                return (
                                                    <td key={idx} className={`px-4 py-4 ${item.key === 'description' ? 'text-primary-light' : ''}`}>
                                                        {child?.[item.key] || '--'}
                                                    </td>
                                                );
                                            })}
                                            <td className='px-4 py-4'></td>
                                        </tr>
                                    ))}

                                </>
                            )
                        })
                        }
                    </tbody>
                </table>
            </div>
            {paginationData &&
                (() => {
                    const currentPage = paginationData.number;
                    const totalPages = paginationData.totalPages;

                    const getPageList = () => {
                        const pageList: (number | string)[] = [];

                        if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                                pageList.push(i);
                            }
                        } else {
                            pageList.push(1);

                            if (currentPage > 3) {
                                pageList.push('...');
                            }

                            const start = Math.max(2, currentPage - 1);
                            const end = Math.min(totalPages - 1, currentPage + 1);

                            for (let i = start; i <= end; i++) {
                                pageList.push(i);
                            }

                            if (currentPage < totalPages - 2) {
                                pageList.push('...');
                            }

                            pageList.push(totalPages);
                        }

                        return pageList;
                    };

                    return (
                        <div className='flex justify-between items-center text-sm text-[#8F8F8F] dark:text-primary-dark px-5 py-2 mt-1 border-t border-[#DCDCDD]'>
                            {/* <div>
                                {paginationData.totalElements > 0 && (
                                    <span>
                                        {1 + ((currentPage - 1) * paginationData.size)}–
                                        {Math.min(currentPage * paginationData.size, paginationData.totalElements)} of {paginationData.totalElements} items
                                    </span>
                                )}
                            </div> */}
                            <div className='flex items-center gap-2'>
                                <button
                                    disabled={currentPage <= 1}
                                    onClick={() =>
                                        setPaginationState?.({ ...paginationState, pageNumber: currentPage - 1 })
                                    }
                                    className='hover:bg-gray-300 dark:hover:bg-black cursor-pointer px-2 py-1 rounded disabled:opacity-50'
                                >
                                    ◀
                                </button>

                                {getPageList().map((item, idx) => {
                                    if (typeof item === 'string') {
                                        return <span key={idx} className='px-2'>...</span>;
                                    }
                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => setPaginationState?.({ ...paginationState, pageNumber: item })}
                                            className={`px-3 py-1 rounded transition-colors duration-200 ${item === currentPage ? 'bg-gray-400 text-white dark:bg-white dark:text-black' : 'hover:bg-gray-300 dark:hover:bg-black'}`}
                                        >
                                            {item}
                                        </button>
                                    );
                                })}

                                <button
                                    disabled={!paginationData?.hasNextPage}
                                    onClick={() =>
                                        setPaginationState?.({ ...paginationState, pageNumber: currentPage + 1 })
                                    }
                                    className='hover:bg-gray-300 dark:hover:bg-black cursor-pointer px-2 py-1 rounded disabled:opacity-50'
                                >
                                    ▶
                                </button>
                            </div>
                        </div>
                    );
                })()
            }

        </div>
    );
};

export default TransactionsTable;
